import React from 'react';
import { PropTypes } from 'prop-types';
import {
  styled,
  alpha,
  ThemeProvider,
  StyledEngineProvider,
} from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
  AppBar as MUIAppBar,
  Toolbar,
  Grid,
  Typography,
  MenuItem,
  ListItemText,
  Paper,
} from '@mui/material';
import clsx from 'clsx';
import Icon from '@mdi/react';
import {
  mdiHelpCircle,
  mdiMessageAlert,
  mdiAccountCircle,
  mdiMenuDown,
  mdiHomeCircle,
  mdiCloudCircle,
} from '@mdi/js';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  theme as daaasUITheme,
  darkTheme,
  Menu,
  Link,
  CanadaHeader,
  Button,
} from 'daaas-ui';
import { useMsal } from '@azure/msal-react';
import AxiosStatic from 'axios';
import { useAxios } from '../Provider/AxiosProvider';
import { config } from '../../Config/AppConfig';
import Breadcrumbs from './Breadcrumbs';
import {
  HelpDrawer,
  SupportDialog,
  FeedbackDialog,
  ProfileDialog,
  TermsAndConditionsDialog,
  MsalExpiredSessionAlert,
} from '../UI/Dialogs';
import MobileMenuDrawer from './MobileMenuDrawer';
import { handleSignOut, handleSignIn } from '../../utils/handleSignInOut';
import ContentContainer from './ContentContainer';
import TwoWayMap from '../../utils/TwoWayMap';

const Root = styled('div')(({ theme }) => ({
  '& .app-bar': {
    zIndex: 1200,
    backgroundColor: theme.palette.common.white,
  },

  '& .app-bar__gridContainer': {
    maxWidth: theme.breakpoints.values.lg,
    margin: 'auto',
  },

  '& .app-bar__branding': {
    '& img': {
      height: theme.spacing(4),
    },
  },

  '& .app-bar__actions': {
    display: 'flex',
    alignItems: 'center',
  },

  '& .app-bar__toolbar': {
    paddingLeft: theme.spacing(0),
    paddingRight: theme.spacing(0),
  },

  '& .app-bar__header': {
    textAlign: 'left',
    boxSizing: 'border-box',
    borderRadius: 0,
  },

  '& .app-bar__headerGrey': {
    backgroundColor: theme.palette.grey[100],
    boxShadow: 'none',
  },

  '& .app-bar__navMenu': {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    padding: 0,
    '&>li': {
      display: 'inline-block',
    },
  },

  '& .app-bar__menuButton': {
    minHeight: `${theme.spacing(8)} !important`,
    borderRadius: 0,
    padding: theme.spacing(1, 2),
    '&.app-bar__currentPage': {
      padding: theme.spacing(1, 2, 0.5, 2),
      borderBottom: `${theme.spacing(0.5)} solid`,
      borderBottomColor: darkTheme.palette.background.paper,
    },
  },
  '& .app-bar__menuItem.Mui-selected': {
    padding: theme.spacing(1, 2, 1, 1.5),
    borderLeft: `${theme.spacing(0.5)} solid`,
    borderLeftColor: darkTheme.palette.background.paper,
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
  '& .app-bar__hubBtnXs': {
    flexGrow: 1,
    maxWidth: '100px',
    '& .MuiButton-startIcon': {
      margin: theme.spacing(0),
    },
  },
}));

export default function AppBar({ auth = false, username = '' }) {
  const { t, i18n } = useTranslation(['common', 'pricing']);
  const { instance } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;

  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);
  const axios = useAxios(true);

  const [open, setOpen] = React.useState({
    helpDrawer: false,
    menuDrawer: false,
    feedbackDialog: false,
    supportDialog: false,
    profileDialog: false,
    termsAndConditionsDialog: false,
    msalExpiredSessionAlert: false,
  });

  const [menuAnchor, setMenuAnchor] = React.useState({
    profile: null,
    env: null,
    mobile: null,
  });

  const [userInExtDasUserAccessAadGroup, setUserInExtDasUserAccessAadGroup] =
    React.useState(false);
  const [userInExtVdlUserAccessAadGroup, setUserInExtVdlUserAccessAadGroup] =
    React.useState(false);

  const isSmScreen = windowWidth < daaasUITheme.breakpoints.values.md;
  const isXsScreen = windowWidth < daaasUITheme.breakpoints.values.sm;

  const MAX_CHAR = isXsScreen ? 15 : 20;
  const isLongUsername = username ? username.length > MAX_CHAR : false;
  const shortenedUsername = username ? `${username.slice(0, MAX_CHAR)}...` : '';

  const pathTwoWayMap = new TwoWayMap({
    '/data-analytics-services/overview': '/services-analyse-donnees/apercu',
    '/data-analytics-services/aboutdas': '/services-analyse-donnees/apropossad',
    '/data-analytics-services/aaw': '/services-analyse-donnees/etaa',
    '/data-analytics-services/cae': '/services-analyse-donnees/eac',
    '/data-analytics-services/gae': '/services-analyse-donnees/eag',
    '/data-analytics-services/hub': '/services-analyse-donnees/guichet',
    '/data-analytics-services/vdl-hub': '/services-analyse-donnees/guichet-lvd',
    '/data-analytics-services/vdl-hub/upload':
      '/services-analyse-donnees/guichet-lvd/televerser',
    '/data-analytics-services/vdl-hub/download':
      '/services-analyse-donnees/guichet-lvd/telecharger',
    '/data-analytics-services/pricing': '/services-analyse-donnees/cout',
    '/data-analytics-services/faq': '/services-analyse-donnees/faq',
    '/data-analytics-services/terms-and-conditions':
      '/services-analyse-donnees/conditions-d-utilisation',
  });

  const toggleLanguage = () => {
    navigate(pathTwoWayMap.get(path), {
      replace: true,
      state: { languageChange: true },
    });
  };

  const toggleDialog = (element, value) => {
    setOpen({ ...open, [element]: value });
  };

  const agreedToTermsAndConditions = () => {
    handleSignIn(instance, i18n.language);
  };

  const openDrawer = (element) => {
    setOpen({ ...open, [element]: true });
  };

  const closeDrawer = (element) => {
    setOpen({ ...open, [element]: false });
  };

  const openMenu = (e, menu) => {
    setMenuAnchor({ ...menuAnchor, [menu]: e.currentTarget });
  };

  const closeMenu = (menu) => {
    setMenuAnchor({ ...menuAnchor, [menu]: null });
  };

  React.useEffect(() => {
    // Detect screen size
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    window.addEventListener('ScreenOrientation', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('ScreenOrientation', handleResize);
    };
  }, []);

  const checkMemberGroups = () => {
    axios
      .get(config.api.API_USER_CHECK_MEMBER_GROUPS)
      .then((result) => {
        if (result.data.groupList.includes('DAS')) {
          setUserInExtDasUserAccessAadGroup(true);
        }
        if (result.data.groupList.includes('VDL')) {
          setUserInExtVdlUserAccessAadGroup(true);
        }
      })
      .catch((error) => {
        // If we have a cancellation error, it is due to missing authorization header.
        if (AxiosStatic.isCancel(error)) {
          // eslint-disable-next-line no-console
          // console.log('IN APPBAR.JS AxiosStatic.isCancel');
          toggleDialog('msalExpiredSessionAlert', true);
        } else {
          // eslint-disable-next-line no-console
          console.log(`error is: ${error}`);
        }
      });
  };

  React.useEffect(() => {
    if (auth) {
      checkMemberGroups();
    }
  }, [auth]);

  const accountMenuItems = () => {
    // if user is authenticated
    if (auth) {
      return [
        <MenuItem
          onClick={() => {
            toggleDialog('profileDialog', true);
          }}
          key="2"
        >
          <ListItemText
            primary={
              <Typography variant="body2">{t('common:profile')}</Typography>
            }
          />
        </MenuItem>,
        <MenuItem
          onClick={() => {
            closeMenu('profile');
            handleSignOut(instance, i18n.language, navigate, location);
          }}
          key="3"
        >
          <ListItemText
            primary={
              <Typography variant="body2">{t('common:signOut')}</Typography>
            }
          />
        </MenuItem>,
      ];
    }

    // if user is not authenticated, there's no account menu on the page, it's just a signin button
    return null;
  };

  const currentPageEnv = () => {
    const envPaths = [
      '/data-analytics-services/aaw',
      '/services-analyse-donnees/etaa',
      '/data-analytics-services/cae',
      '/services-analyse-donnees/eac',
      '/data-analytics-services/gae',
      '/services-analyse-donnees/eag',
    ];
    return envPaths.includes(path);
  };

  return (
    <Root>
      <MUIAppBar className="app-bar" color="default" position="static">
        <CanadaHeader lang={i18n.language} toggleLanguage={toggleLanguage}>
          {!isSmScreen && (
            <ul className="list-horizontal">
              <li>
                <Button
                  sx={{ mr: 1 }}
                  color="default"
                  variant="text"
                  startIcon={<Icon path={mdiHelpCircle} size={1} />}
                  onClick={() => openDrawer('helpDrawer')}
                >
                  {t('common:help')}
                </Button>
              </li>
              <li>
                <Button
                  color="default"
                  variant="text"
                  startIcon={<Icon path={mdiMessageAlert} size={1} />}
                  onClick={() => toggleDialog('feedbackDialog', true)}
                  sx={{ mr: -1 }}
                >
                  {t('common:feedback')}
                </Button>
              </li>
            </ul>
          )}
        </CanadaHeader>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={darkTheme}>
            <Paper className="app-bar__header">
              <ContentContainer paddingBottom={false}>
                <Toolbar className="app-bar__toolbar">
                  <Link
                    href={`${t('common:/urlPrefix')}${t('common:/overview')}`}
                    variant="h2"
                    color="inherit"
                    underline="hover"
                  >
                    {t('common:daaas')}
                  </Link>
                </Toolbar>
              </ContentContainer>
            </Paper>
          </ThemeProvider>
        </StyledEngineProvider>
        <Paper className="app-bar__header app-bar__headerGrey">
          <ContentContainer paddingBottom={false}>
            <Toolbar component="nav" className="app-bar__toolbar">
              <Grid
                container
                justifyContent="space-between"
                alignItems="center"
                wrap="nowrap"
              >
                <Grid item>
                  <ul
                    className="app-bar__navMenu"
                    aria-label={t('common:menu')}
                  >
                    {!isSmScreen ? (
                      <>
                        <li>
                          <Button
                            component={RouterLink}
                            to={`${t('common:/urlPrefix')}${t('common:/overview')}`}
                            color="default"
                            className={clsx('app-bar__menuButton', {
                              'app-bar__currentPage':
                                path === '/data-analytics-services/overview' ||
                                path === '/services-analyse-donnees/apercu',
                            })}
                            aria-current={
                              path === '/data-analytics-services/overview' ||
                              path === '/services-analyse-donnees/apercu'
                                ? 'page'
                                : false
                            }
                            sx={{ ml: -2 }}
                          >
                            {t('common:overview')}
                          </Button>
                        </li>
                        <li>
                          <Button
                            component={RouterLink}
                            to={`${t('common:/urlPrefix')}${t('common:/aboutdas')}`}
                            color="default"
                            className={clsx('app-bar__menuButton', {
                              'app-bar__currentPage':
                                path === '/data-analytics-services/aboutdas' ||
                                path === '/services-analyse-donnees/apropossad',
                            })}
                            aria-current={
                              path === '/data-analytics-services/aboutdas' ||
                              path === '/services-analyse-donnees/apropossad'
                                ? 'page'
                                : false
                            }
                          >
                            {t('common:about')}
                          </Button>
                        </li>
                        <li>
                          <Button
                            color="default"
                            className={clsx('app-bar__menuButton', {
                              'app-bar__currentPage': currentPageEnv(),
                            })}
                            aria-controls="env-menu"
                            aria-haspopup="true"
                            aria-expanded={Boolean(menuAnchor.env)}
                            onClick={(e) => openMenu(e, 'env')}
                            endIcon={<Icon path={mdiMenuDown} size={1} />}
                          >
                            {t('common:environments')}
                          </Button>
                          <Menu
                            id="env-menu"
                            placement="bottom-start"
                            open={Boolean(menuAnchor.env)}
                            anchorEl={menuAnchor.env}
                            handleClose={() => closeMenu('env')}
                          >
                            <MenuItem
                              component={RouterLink}
                              to={`${t('common:/urlPrefix')}${t('common:/aaw')}`}
                              onClick={() => {
                                closeMenu('env');
                              }}
                              selected={
                                path === '/data-analytics-services/aaw' ||
                                path === '/services-analyse-donnees/etaa'
                              }
                              className="app-bar__menuItem"
                              aria-current={
                                path === '/data-analytics-services/aaw' ||
                                path === '/services-analyse-donnees/etaa'
                                  ? 'page'
                                  : false
                              }
                            >
                              <ListItemText
                                primary={
                                  <Typography variant="body2">
                                    {t('common:aaw')}
                                  </Typography>
                                }
                              />
                            </MenuItem>
                            <MenuItem
                              component={RouterLink}
                              to={`${t('common:/urlPrefix')}${t('common:/cae')}`}
                              onClick={() => {
                                closeMenu('env');
                              }}
                              selected={
                                path === '/data-analytics-services/cae' ||
                                path === '/services-analyse-donnees/eac'
                              }
                              className="app-bar__menuItem"
                              aria-current={
                                path === '/data-analytics-services/cae' ||
                                path === '/services-analyse-donnees/eac'
                                  ? 'page'
                                  : false
                              }
                            >
                              <ListItemText
                                primary={
                                  <Typography variant="body2">
                                    {t('common:cae')}
                                  </Typography>
                                }
                              />
                            </MenuItem>
                            <MenuItem
                              component={RouterLink}
                              to={`${t('common:/urlPrefix')}${t('common:/gae')}`}
                              onClick={() => {
                                closeMenu('env');
                              }}
                              selected={
                                path === '/data-analytics-services/gae' ||
                                path === '/services-analyse-donnees/eag'
                              }
                              className="app-bar__menuItem"
                              aria-current={
                                path === '/data-analytics-services/gae' ||
                                path === '/services-analyse-donnees/eag'
                                  ? 'page'
                                  : false
                              }
                            >
                              <ListItemText
                                primary={
                                  <Typography variant="body2">
                                    {t('common:gae')}
                                  </Typography>
                                }
                              />
                            </MenuItem>
                          </Menu>
                        </li>
                        <li>
                          <Button
                            component={RouterLink}
                            to={`${t('common:/urlPrefix')}${t('common:/pricing')}`}
                            color="default"
                            className={clsx('app-bar__menuButton', {
                              'app-bar__currentPage':
                                path === '/data-analytics-services/pricing' ||
                                path === '/services-analyse-donnees/cout',
                            })}
                            aria-current={
                              path === '/data-analytics-services/pricing' ||
                              path === '/services-analyse-donnees/cout'
                                ? 'page'
                                : false
                            }
                          >
                            {t('pricing:pricing')}
                          </Button>
                        </li>
                      </>
                    ) : (
                      <li>
                        <Button
                          color="default"
                          className="app-bar__menuButton"
                          onClick={() => openDrawer('menuDrawer')}
                          endIcon={<Icon path={mdiMenuDown} size={1} />}
                          sx={{ ml: -2 }}
                        >
                          {t('common:menu')}
                        </Button>
                      </li>
                    )}
                  </ul>
                </Grid>
                <Grid
                  item
                  sx={{
                    flexGrow: 1,
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  {auth ? (
                    // if user is authenticated
                    <>
                      {userInExtDasUserAccessAadGroup ? (
                        <Button
                          variant="contained"
                          color="primary"
                          className={clsx({
                            'app-bar__hubBtnXs': isXsScreen,
                          })}
                          sx={{ mr: 1 }}
                          startIcon={<Icon path={mdiHomeCircle} size={1} />}
                          href={`${t('common:/urlPrefix')}${t('common:/hub')}`}
                        >
                          <span
                            className={clsx({
                              'screen-reader-text': isXsScreen,
                            })}
                          >
                            {t('common:myHub')}
                          </span>
                        </Button>
                      ) : null}
                      {userInExtVdlUserAccessAadGroup ? (
                        <Button
                          variant="contained"
                          color="secondary"
                          className={clsx({
                            'app-bar__hubBtnXs': isXsScreen,
                          })}
                          sx={{ mr: 1 }}
                          startIcon={<Icon path={mdiCloudCircle} size={1} />}
                          href={`${t('common:/urlPrefix')}${t('common:/vdlHub')}`}
                        >
                          <span
                            className={clsx({
                              'screen-reader-text': isXsScreen,
                            })}
                          >
                            {t('common:vdlHub')}
                          </span>
                        </Button>
                      ) : null}
                      <Button
                        variant="text"
                        color="primary"
                        className="edge-end"
                        aria-controls="account-menu"
                        aria-haspopup="true"
                        aria-expanded={Boolean(menuAnchor.profile)}
                        endIcon={<Icon path={mdiMenuDown} size={1} />}
                        onClick={(e) => openMenu(e, 'profile')}
                        title={isLongUsername ? username : null}
                      >
                        <span id="username">
                          {isLongUsername ? shortenedUsername : username}
                        </span>
                      </Button>
                      <Menu
                        id="account-menu"
                        open={Boolean(menuAnchor.profile)}
                        anchorEl={menuAnchor.profile}
                        handleClose={() => closeMenu('profile')}
                      >
                        {accountMenuItems()}
                      </Menu>
                    </>
                  ) : (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<Icon path={mdiAccountCircle} size={1} />}
                      onClick={() =>
                        toggleDialog('termsAndConditionsDialog', true)
                      }
                    >
                      {t('common:signIn')}
                    </Button>
                  )}
                </Grid>
              </Grid>
            </Toolbar>
          </ContentContainer>
        </Paper>
        <ContentContainer paddingBottom={false}>
          <Toolbar component="nav" className="app-bar__toolbar">
            <Grid
              container
              justifyContent="space-between"
              alignItems="center"
              className="app-bar__gridContainer"
            >
              <Grid item>
                <Breadcrumbs />
              </Grid>
            </Grid>
          </Toolbar>
        </ContentContainer>
      </MUIAppBar>

      {/* Help drawer */}
      <HelpDrawer
        open={open.helpDrawer}
        closeDrawer={() => closeDrawer('helpDrawer')}
        toggleSupportDialog={() =>
          toggleDialog('supportDialog', !open.supportDialog)
        }
      />
      {/* Support dialog */}
      <SupportDialog
        open={open.supportDialog}
        toggleDialog={() => toggleDialog('supportDialog', !open.supportDialog)}
      />
      {/* Feedback dialog */}
      <FeedbackDialog
        open={open.feedbackDialog}
        toggleDialog={() =>
          toggleDialog('feedbackDialog', !open.feedbackDialog)
        }
      />
      {/* Profile dialog */}
      <ProfileDialog
        open={open.profileDialog}
        toggleDialog={() => toggleDialog('profileDialog', !open.profileDialog)}
      />
      {/* Terms And Conditions dialog */}
      <TermsAndConditionsDialog
        open={open.termsAndConditionsDialog}
        toggleDialog={() =>
          toggleDialog(
            'termsAndConditionsDialog',
            !open.termsAndConditionsDialog
          )
        }
        agreedToTermsAndConditions={() => agreedToTermsAndConditions()}
      />
      {/* MsalExpiredSessionAlert */}
      <MsalExpiredSessionAlert
        open={open.msalExpiredSessionAlert}
        toggleDialog={() =>
          toggleDialog('msalExpiredSessionAlert', !open.msalExpiredSessionAlert)
        }
      />

      {/* Menu drawer for mobile */}
      <MobileMenuDrawer
        open={open.menuDrawer}
        toggleDialog={toggleDialog}
        openDrawer={openDrawer}
        closeDrawer={closeDrawer}
      />
    </Root>
  );
}

AppBar.propTypes = {
  auth: PropTypes.bool,
  username: PropTypes.string,
};
